import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import Autocomplete from '@hobs/components/Autocomplete';
import Input from '@hobs/components/Input';
import InputLabel from '@hobs/components/InputLabel';
import Icon from '@hobs/components/Icon';
import { withTranslation } from 'react-i18next';
import { Field } from 'formik';
import './LocationDetails.scss';
import PropTypes from 'prop-types';

const MAP_ENABLED = true;

const LocationDetails = ({
  locationItems = [],
  onQueryChange = () => {},
  setFieldValue = () => {},
  t,
  initialLocation = {},
  defaultQuery = '',
  disabled,
  schema = {
    location: 'location',
    locationComment: 'locationComment'
  },
  isExpandable = false,
  editedFieldsHistory = {}
}) => {
  const [location, setLocation] = useState(initialLocation);
  const openLocationWindow = (lat, lon, desc) => event => {
    event.preventDefault();

    window.open(
      `${process.env.PUBLIC_URL}/map.html?lat=${lat}&lon=${lon}&desc=${desc}`,
      'newwindow',
      'width=640,height=480'
    );
  };

  const hasLocationData = () => {
    return location && location.original;
  };

  const [isOpen, setOpen] = useState(false);

  useEffect(() => {
    setLocation(initialLocation);
  }, [initialLocation]);

  return (
    <div
      className={cx('location-details', {
        'location-details--expandable': isExpandable,
        'location-details--isOpen': isExpandable && isOpen === true
      })}
    >
      <div className="location-details--row">
        <button type="button" onClick={() => setOpen(!isOpen)}>
          <Icon icon="ArrowDown" />
        </button>

        <Field
          label="Location"
          placeholder="Location"
          name={schema.location}
          items={locationItems}
          onItemSelect={item => {
            const value = item ? item : initialLocation;
            setFieldValue(
              schema.location,
              Object.keys(value).length === 0 ? null : value
            );
            setLocation(value);
          }}
          noResultsMessage={t('Location not found')}
          onQueryChange={onQueryChange}
          component={Autocomplete}
          defaultQuery={defaultQuery}
          disabled={disabled}
          indicationMessage={editedFieldsHistory.location}
        />

        <Field
          placeholder={t('Location comment')}
          name={schema.locationComment}
          setFieldValue={setFieldValue}
          component={Input}
          disabled={disabled}
          indicationMessage={editedFieldsHistory.locationComment}
        >
          {t('Location comment')}
        </Field>

        {MAP_ENABLED &&
          Object.keys(location).length > 0 &&
          hasLocationData() &&
          location.original.latitude &&
          location.original.longtitude && (
            <div className="location-details--location">
              <a
                onClick={openLocationWindow(
                  location.original.latitude,
                  location.original.longtitude,
                  location.original.name
                )}
                href="/#"
                title=""
              >
                <Icon icon="location" />
              </a>
            </div>
          )}
      </div>

      <div className="location-details--row location-details--data">
        <div>
          <InputLabel indicationMessage={editedFieldsHistory.region}>
            {t('Region')}
          </InputLabel>
          <div>
            {hasLocationData() && location.original.region
              ? location.original.region.name
              : '-'}
          </div>
        </div>
        <div>
          <InputLabel indicationMessage={editedFieldsHistory.site}>
            {t('Site')}
          </InputLabel>
          <div>{hasLocationData() ? location.original.site : '-'}</div>
        </div>
        <div>
          <InputLabel indicationMessage={editedFieldsHistory.latitude}>
            {t('Latitude')}
          </InputLabel>
          <div>{hasLocationData() ? location.original.latitude : '-'}</div>
        </div>
        <div>
          <InputLabel indicationMessage={editedFieldsHistory.longitude}>
            {t('Longitude')}
          </InputLabel>
          <div>{hasLocationData() ? location.original.longtitude : '-'}</div>
        </div>
      </div>
    </div>
  );
};

LocationDetails.propTypes = {
  defaultQuery: PropTypes.string,
  locationItems: PropTypes.array,
  initialLocation: PropTypes.object,
  onQueryChange: PropTypes.func,
  onCommentChange: PropTypes.func,
  setFieldValue: PropTypes.func,
  t: PropTypes.func,
  schema: PropTypes.shape({
    location: PropTypes.string,
    locationComment: PropTypes.string
  }),
  isExpandable: PropTypes.bool,
  disabled: PropTypes.bool,
  editedFieldsHistory: PropTypes.object
};

export default withTranslation()(LocationDetails);
